import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { FormInput, Loader, Notice, OnChangePseudoEvent } from '@calm-web/design-system';

import BackButton from '@/components/b2b/BackButton';
import { BlueLine } from '@/components/b2b/Welcome/styles';
import { CopyItem } from '@/components/valuePropList';
import { useAnalytics } from '@/hooks/analytics';
import { useAuthFormInputs } from '@/hooks/forms/useAuthFormInputs';
import { useUserState } from '@/hooks/store';
import genericMessages from '@/messages/messages';
import {
	hasSubscriptionConflictingWithB2BRedemption,
	isB2BSubscriptionWithPartner,
} from '@/utils/subscriptions';
import { validate } from '@/utils/ui/validator';

import accountMessages from '../Account/messages';
import { AccountValuePropsList, ValuePropsListWrapper } from '../Account/styles';
import { IntegrationType, SignupScreenProps } from '../shared/types';
import messages from './messages';
import {
	CtaText,
	Description,
	EligibilityContentWrapper,
	EligibilityContentWrapperRevamp,
	EligibilityTitle,
	FullWidthButton,
	InvalidIdentifier,
	Legal,
	PageWrap,
	SubmitButton,
	TwoColumnWrapper,
} from './styles';
import {
	EligibilityDescriptorKey,
	EligibilityDescriptors,
	EligibilityErrors,
	isEligibilityDescriptorKey,
	isEligibilityErrorKey,
} from './types';

export function formatUserIdDescriptor(
	user_id_descriptor: string | undefined,
	formatMessage: IntlShape['formatMessage'],
) {
	// lowercase and remove whitespace to handle multiple variations of user_id_descriptor in db
	const descriptor = user_id_descriptor?.toLowerCase().replace(/\s/g, '');
	if (descriptor && isEligibilityDescriptorKey(descriptor)) {
		return formatMessage(EligibilityDescriptors[descriptor as EligibilityDescriptorKey]);
	}
	return user_id_descriptor ?? 'identifier';
}

export function formatUserIdDescriptorRevamp(
	user_id_descriptor: string | undefined,
	formatMessage: IntlShape['formatMessage'],
	isForLabel = false,
) {
	let result = formatUserIdDescriptor(user_id_descriptor, formatMessage);
	if (result.includes('code')) {
		result = 'code';
	}

	if (isForLabel) {
		return result.replace(/\b\w/g, (char: string) => char.toUpperCase());
	}

	return result.toLowerCase();
}

const Eligibility = ({
	onScreenChange,
	partnerDetails,
	redeem,
	error,
	isSubmitting,
	isRevampRedempFlow,
}: Pick<
	SignupScreenProps,
	'onScreenChange' | 'partnerDetails' | 'redeem' | 'error' | 'isSubmitting' | 'isRevampRedempFlow'
>) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [inputFields, inputActions] = useAuthFormInputs(['uniqueId'] as const);
	const [showError, setShowError] = useState(false);
	const user = useUserState();
	const { name, user_id_descriptor, integration_type, pageInfo, id: partnerID } = partnerDetails;
	const [validationError, setValidationError] = useState('');

	const { onChange, onBlur, onFocus } = inputActions;

	useEffect(
		() => {
			if (hasSubscriptionConflictingWithB2BRedemption(user?.subscription)) {
				onScreenChange('sorry');
			}
			if (isB2BSubscriptionWithPartner(partnerID, user?.subscription)) {
				const partnerUserId = user?.subscription?.b2b_details?.partner_user_id;
				if (partnerUserId && user?.subscription?.is_renewable) {
					redeem(partnerDetails, { partner_user_id: partnerUserId }).catch(() => {});
				} else if (user?.subscription?.valid) {
					onScreenChange('success');
				}
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		if (isSubmitting) {
			setShowError(false);
		} else {
			setShowError(true);
		}
	}, [isSubmitting]);

	useEffect(() => {
		if (error) {
			logEvent({
				eventName: 'B2B Signup : Redemption : Error',
				eventProps: {
					partner_id: partnerDetails.id as string,
					integration_type: partnerDetails.integration_type as string,
					...(user_id_descriptor && { user_id_descriptor }),
					error_code: error.code,
				},
			});
		}
	}, [error, logEvent, partnerDetails, user_id_descriptor]);

	async function submitEligibility(e: SyntheticEvent): Promise<void> {
		e.preventDefault();
		e.stopPropagation();

		if (
			integration_type === IntegrationType.ELIGIBILITY_FILE &&
			user_id_descriptor?.toLowerCase() === 'email' &&
			!validate('email', inputFields.uniqueId?.value)
		) {
			setValidationError(formatMessage(messages.validationError, { uniqueId: 'email' }));
			return;
		}

		if (integration_type === IntegrationType.GROUP_CODE) {
			await redeem(partnerDetails, { group_code: inputFields.uniqueId?.value });
		} else {
			await redeem(partnerDetails, { partner_user_id: inputFields.uniqueId?.value });
		}
	}

	function handleInputChange(e: OnChangePseudoEvent): void {
		setShowError(false);
		onChange(e);
	}

	function handleInputFocus(e: React.FocusEvent<HTMLInputElement>): void {
		setValidationError('');
		onFocus(e);
	}

	function renderError(): React.ReactNode {
		if (
			inputFields.uniqueId.value &&
			showError &&
			error?.code &&
			isEligibilityErrorKey(error.code) &&
			EligibilityErrors[error.code]
		) {
			return (
				<Notice describedBy="submit-eligibility-form">
					{formatMessage(EligibilityErrors[error.code], {
						uniqueId: formatUserIdDescriptor(user_id_descriptor, formatMessage),
						supportlink: (...chunks: ReactNode[]) => (
							<a
								href="https://support.calm.com/hc/en-us?solvvyProvidedByEmployer"
								target="_blank"
								rel="noopener noreferrer"
							>
								{chunks}
							</a>
						),
					})}
				</Notice>
			);
		}
		return null;
	}

	function handleInputBlur(e: React.FocusEvent<HTMLInputElement>): void {
		const inputEvent = e as React.FocusEvent<HTMLInputElement>;
		onBlur(inputEvent, { value: inputEvent.currentTarget.value });

		if (
			integration_type === IntegrationType.ELIGIBILITY_FILE &&
			user_id_descriptor?.toLowerCase() === 'email' &&
			!validate('email', inputFields.uniqueId?.value)
		) {
			setValidationError(formatMessage(messages.validationError, { uniqueId: 'email' }));
			return;
		}

		if (!inputEvent.currentTarget.value) {
			const descriptor = formatUserIdDescriptorRevamp(user_id_descriptor, formatMessage);
			const errorMessage = formatMessage(messages.validationError, { uniqueId: descriptor });
			setValidationError(errorMessage);
		} else {
			setValidationError('');
		}
	}

	const instructionsMessage = pageInfo.messages?.instructions
		? pageInfo.messages.instructions
		: messages.instructions;

	if (
		user?.subscription?.is_renewable &&
		user?.subscription?.b2b_details?.partner_user_id &&
		isB2BSubscriptionWithPartner(partnerID, user?.subscription)
	) {
		return (
			<EligibilityContentWrapper>
				<Loader color="gray8" />
			</EligibilityContentWrapper>
		);
	}

	if (isRevampRedempFlow) {
		const valueProps: Array<CopyItem> = [
			{
				id: 'valueProp1',
				header: accountMessages.valueProp1Head,
				body: accountMessages.valueProp1Body,
			},
			{
				id: 'valueProp2',
				header: accountMessages.valueProp2Head,
				body: accountMessages.valueProp2Body,
			},
			{
				id: 'valueProp3',
				header: accountMessages.valueProp3Head,
				body: accountMessages.valueProp3Body,
			},
		];

		return (
			<PageWrap>
				<TwoColumnWrapper>
					<ValuePropsListWrapper style={{ marginTop: 0 }} isEligibilityPage={true}>
						<EligibilityTitle>{formatMessage(messages.valuePropHeader)}</EligibilityTitle>
						<AccountValuePropsList copy={valueProps} />
					</ValuePropsListWrapper>
					<EligibilityContentWrapperRevamp>
						<CtaText>
							{formatMessage(messages.titleRevamp, {
								uniqueId: formatUserIdDescriptorRevamp(user_id_descriptor, formatMessage),
							})}
						</CtaText>
						<Description isRevampRedempFlow={isRevampRedempFlow}>
							{formatMessage(messages.instructionsRevamp, {
								uniqueId: formatUserIdDescriptorRevamp(user_id_descriptor, formatMessage),
								partnerName: name,
								planName: 'Calm Premium', // TODO: Figure out how to get this in a later ticket
								subscriptionDuration: '1 year',
							})}
						</Description>
						<form onSubmit={submitEligibility} id="submit-eligibility-form">
							<FormInput
								name="uniqueId"
								label={formatUserIdDescriptorRevamp(user_id_descriptor, formatMessage, true)}
								onChange={handleInputChange}
								onBlur={handleInputBlur}
								onFocus={handleInputFocus}
								value={inputFields.uniqueId?.value}
								isValid={Boolean(!validationError)}
							/>
							{validationError && <InvalidIdentifier>{validationError}</InvalidIdentifier>}{' '}
							{pageInfo.messages?.legal ? <Legal>{formatMessage(pageInfo.messages.legal)}</Legal> : null}
							{renderError()}
							<FullWidthButton
								backgroundColor="buttonPrimaryBg"
								type="submit"
								isDisabled={!inputFields.uniqueId?.value || isSubmitting}
								isLoading={isSubmitting}
								data-testid="b2b-partneruserid-submit"
							>
								{formatMessage(genericMessages.submit)}{' '}
								{formatUserIdDescriptorRevamp(user_id_descriptor, formatMessage)}
							</FullWidthButton>
						</form>
					</EligibilityContentWrapperRevamp>
				</TwoColumnWrapper>
			</PageWrap>
		);
	}

	return (
		<EligibilityContentWrapper>
			<BackButton onScreenChange={onScreenChange} />
			<CtaText>
				{formatMessage(messages.title, {
					uniqueId: formatUserIdDescriptor(user_id_descriptor, formatMessage),
				})}
			</CtaText>
			<Description>
				{formatMessage(instructionsMessage, {
					uniqueId: formatUserIdDescriptor(user_id_descriptor, formatMessage),
					partnerName: name,
					planName: 'Calm Premium', // TODO: Figure out how to get this in a later ticket
					subscriptionDuration: '1 year',
				})}
			</Description>
			<BlueLine />
			<form onSubmit={submitEligibility} id="submit-eligibility-form">
				<FormInput
					name="uniqueId"
					label={formatUserIdDescriptor(user_id_descriptor, formatMessage)}
					onChange={handleInputChange}
					onBlur={e => {
						// We feel confident casting to HTMLInputElement here because that's what's under the hood
						const inputEvent = e as React.FocusEvent<HTMLInputElement>;
						onBlur(inputEvent, { value: inputEvent.currentTarget.value });
					}}
					onFocus={onFocus}
					value={inputFields.uniqueId?.value}
					isValid={Boolean(inputFields.uniqueId?.value)}
				/>
				{pageInfo.messages?.legal ? <Legal>{formatMessage(pageInfo.messages.legal)}</Legal> : null}
				{renderError()}
				<SubmitButton
					backgroundColor="buttonLime"
					type="submit"
					isDisabled={!inputFields.uniqueId?.value || isSubmitting}
					legacyOnClick={submitEligibility}
					isLoading={isSubmitting}
					data-testid="b2b-partneruserid-submit"
				>
					{formatMessage(genericMessages.submit)}
				</SubmitButton>
			</form>
		</EligibilityContentWrapper>
	);
};

export default Eligibility;
